import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Loader2, FileText, Eye, ExternalLink, Info, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const ConsignmentContractDialog = ({ open, onClose, deliverableId, onGenerated, forceRegenerate }) => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [additionalFees, setAdditionalFees] = useState('');
  const [additionalTerms, setAdditionalTerms] = useState('');
  const [withdrawalShipin, setWithdrawalShipin] = useState('');
  const [withdrawalSale, setWithdrawalSale] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [termsDocUrl, setTermsDocUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [step, setStep] = useState('configure'); // 'configure' | 'preview'
  const [existingFileId, setExistingFileId] = useState(null);
  const [isLiveDraft, setIsLiveDraft] = useState(false);

  useEffect(() => {
    if (open && deliverableId) {
      setPreviewUrl(null);
      setExistingFileId(null);
      setIsLiveDraft(false);
      loadData();
    }
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [open, deliverableId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/consignment-contracts/preview-data/${deliverableId}`);
      const d = res.data;
      setPreviewData(d);
      setWithdrawalShipin(d.account?.withdrawal_fee_shipin || d.default_withdrawal_fee_shipin || '');
      setWithdrawalSale(d.account?.withdrawal_fee_sale || d.default_withdrawal_fee_sale || '');

      // Check if contract already generated (and not forcing regeneration)
      if (d.contract_file_id && !forceRegenerate) {
        setExistingFileId(d.contract_file_id);
        // Restore saved contract details if available
        if (d.contract_data?.contract_details) {
          const cd = d.contract_data.contract_details;
          if (cd.additional_fees) setAdditionalFees(cd.additional_fees);
          if (cd.additional_terms) setAdditionalTerms(cd.additional_terms);
          if (cd.withdrawal_fee_shipin) setWithdrawalShipin(cd.withdrawal_fee_shipin);
          if (cd.withdrawal_fee_sale) setWithdrawalSale(cd.withdrawal_fee_sale);
        }
        // Load existing PDF for preview
        const token = localStorage.getItem('token');
        const fr = await fetch(`${window.location.origin}/api/files/download/${d.contract_file_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (fr.ok) {
          const blob = await fr.blob();
          setPreviewUrl(URL.createObjectURL(blob));
        }
        setStep('preview');
      } else {
        setStep('configure');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load contract data');
      setStep('configure');
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => ({
    consignor: previewData.consignor,
    account: previewData.account,
    sale: previewData.sale,
    horses: previewData.horses,
    terms_and_conditions: previewData.terms_and_conditions,
    terms_file_id: previewData.terms_file_id || null,
    contract_details: {
      effective_date: new Date(effectiveDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      additional_fees: additionalFees,
      additional_terms: additionalTerms,
      withdrawal_fee_shipin: withdrawalShipin,
      withdrawal_fee_sale: withdrawalSale,
    },
  });

  const handlePreview = async () => {
    if (!previewData) return;
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const fr = await fetch(`${window.location.origin}/api/consignment-contracts/preview-pdf/${deliverableId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      if (fr.ok) {
        const blob = await fr.blob();
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
      } else {
        toast.error('Failed to generate preview');
      }
      setIsLiveDraft(true);
      setStep('preview');
    } catch (e) {
      toast.error('Failed to generate preview');
    } finally {
      setGenerating(false);
    }
  };

  const [approving, setApproving] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    try {
      // Save the version first
      await api.post(`/consignment-contracts/generate/${deliverableId}`, buildPayload());
      // Then approve
      const res = await api.post(`/consignment-contracts/approve/${deliverableId}`);
      toast.success(res.data.message || 'Contract approved and sent for internal signature');
      onGenerated?.();
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve contract');
    } finally {
      setApproving(false);
    }
  };

  const fmtAddr = (a) => {
    if (!a) return '—';
    const parts = [a.street, [a.city, a.state, a.zip].filter(Boolean).join(', ')].filter(Boolean);
    return parts.join(', ') || '—';
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl flex flex-col max-h-[90vh] gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {step === 'configure' ? 'Create Consignment Contract' : 'Contract Preview'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : step === 'configure' && previewData ? (
            <>
              {/* Sale Name */}
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sale</Label>
                <p className="text-base font-bold" data-testid="contract-sale-name">{previewData.sale?.name || '—'}</p>
              </div>

              {/* Consignor Details */}
              <div className="border rounded-md p-4 space-y-2 bg-muted/20">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Consignor Details</p>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <div><span className="text-muted-foreground">Name:</span> {previewData.consignor?.name}</div>
                  <div><span className="text-muted-foreground">Email:</span> {previewData.consignor?.email}</div>
                  <div className="col-span-2"><span className="text-muted-foreground">Address:</span> {previewData.consignor?.address_street}, {[previewData.consignor?.address_city, previewData.consignor?.address_state, previewData.consignor?.address_zip].filter(Boolean).join(', ')}</div>
                  <div><span className="text-muted-foreground">Signatory:</span> {previewData.consignor?.signatory_name || <span className="text-yellow-600 italic">Not set</span>}</div>
                  <div><span className="text-muted-foreground">Title:</span> {previewData.consignor?.signatory_title || <span className="text-yellow-600 italic">Not set</span>}</div>
                </div>
                <div className="flex items-start gap-1.5 mt-2 pt-2 border-t">
                  <Info className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <p className="text-xs text-muted-foreground">To make any changes to your Consignor Details, please contact your System Administrator.</p>
                </div>
              </div>

              {/* Account Details */}
              <div className="border rounded-md p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Account Details</p>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => window.location.href = `/crm/accounts/${previewData.account?.id}`} data-testid="edit-account-btn">
                      <ExternalLink className="w-3 h-3 mr-1" /> Edit Account Details
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <div><span className="text-muted-foreground">Name:</span> {previewData.account?.name}</div>
                  <div><span className="text-muted-foreground">Email:</span> {previewData.account?.email || '—'}</div>
                  <div className="col-span-2"><span className="text-muted-foreground">Address:</span> {fmtAddr(previewData.account?.address)}</div>
                  <div><span className="text-muted-foreground">Commission Rate:</span> {previewData.account?.commission_rate_percent ? `${previewData.account.commission_rate_percent}%` : '—'}</div>
                  <div><span className="text-muted-foreground">Min Commission:</span> {previewData.account?.minimum_commission_amount ? `$${Number(previewData.account.minimum_commission_amount).toLocaleString()}` : '—'}</div>
                  <div><span className="text-muted-foreground">Withdrawal (Pre-Ship-In):</span> {withdrawalShipin ? `$${Number(withdrawalShipin).toLocaleString()}` : '—'}</div>
                  <div><span className="text-muted-foreground">Withdrawal (at Sale):</span> {withdrawalSale ? `$${Number(withdrawalSale).toLocaleString()}` : '—'}</div>
                </div>
              </div>

              {/* Contract Details */}
              <div className="border rounded-md p-4 space-y-4">
                <p className="text-sm font-semibold">Contract Details</p>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Effective Date *</Label>
                    <Input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} data-testid="effective-date-input" />
                  </div>
                </div>

                <Button variant="outline" size="sm" onClick={() => {
                  if (previewData.terms_file_id) {
                    if (termsDocUrl) {
                      setShowTerms(!showTerms);
                    } else {
                      const token = localStorage.getItem('token');
                      fetch(`${window.location.origin}/api/files/download/${previewData.terms_file_id}`, { headers: { Authorization: `Bearer ${token}` } })
                        .then(r => r.blob()).then(blob => {
                          setTermsDocUrl(URL.createObjectURL(blob));
                          setShowTerms(true);
                        })
                        .catch(() => toast.error('Failed to load document'));
                    }
                  } else {
                    setShowTerms(!showTerms);
                  }
                }} data-testid="view-terms-btn">
                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                  {showTerms ? 'Hide' : 'View'} Standard General Terms &amp; Conditions
                </Button>

                {showTerms && (
                  <div className="border rounded bg-muted/20 overflow-hidden">
                    {termsDocUrl ? (
                      <object data={termsDocUrl} type="application/pdf" className="w-full" style={{ height: 300 }}>
                        <p className="text-sm text-muted-foreground p-4 text-center">Unable to preview. <a href={termsDocUrl} download className="text-primary underline">Download</a></p>
                      </object>
                    ) : previewData.terms_and_conditions ? (
                      <div className="p-3 max-h-48 overflow-y-auto text-xs prose prose-xs max-w-none" dangerouslySetInnerHTML={{ __html: previewData.terms_and_conditions }} />
                    ) : (
                      <p className="text-xs text-muted-foreground italic p-3">No General Terms &amp; Conditions have been configured. Please add them in Tenant Admin &gt; Settings.</p>
                    )}
                  </div>
                )}

                {/* Horses */}
                <div className="space-y-2">
                  <Label className="text-xs">Horses in this Sale</Label>
                  {previewData.horses?.length > 0 ? (
                    <div className="border rounded overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left px-3 py-1.5 text-xs font-medium">Horse</th>
                            <th className="text-left px-3 py-1.5 text-xs font-medium">Sire</th>
                            <th className="text-left px-3 py-1.5 text-xs font-medium">Dam</th>
                            <th className="text-left px-3 py-1.5 text-xs font-medium">Ownership</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.horses.map((h, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-3 py-1.5 font-medium">{h.name}</td>
                              <td className="px-3 py-1.5">{h.sire || '—'}</td>
                              <td className="px-3 py-1.5">{h.dam || '—'}</td>
                              <td className="px-3 py-1.5">{h.ownership_percent ? `${h.ownership_percent}%` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No horses found for this owner in this sale.</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Additional Fees</Label>
                  <Input value={additionalFees} onChange={e => setAdditionalFees(e.target.value)} placeholder="NONE" data-testid="additional-fees-input" />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Additional Terms</Label>
                  <Textarea value={additionalTerms} onChange={e => setAdditionalTerms(e.target.value)} placeholder="NONE" rows={3} data-testid="additional-terms-input" />
                </div>
              </div>
            </>
          ) : step === 'preview' ? (
            <>
              {/* Status messages */}
              {previewData?.contract_status === 'awaiting_internal_signature' && (
                <div className="border rounded-md p-3 bg-yellow-50 flex items-center gap-2" data-testid="awaiting-sig-banner">
                  <Clock className="w-4 h-4 text-yellow-600 shrink-0" />
                  <p className="text-sm">Awaiting internal signature from <strong>{previewData?.contract_data?.consignor?.signatory_name || 'signatory'}</strong></p>
                </div>
              )}
              {!isLiveDraft && previewData?.contract_internally_signed && (
                <div className="border rounded-md p-3 bg-green-50 flex items-center gap-2" data-testid="internally-signed-banner">
                  <FileText className="w-4 h-4 text-green-600 shrink-0" />
                  <p className="text-sm">Internally signed. Ready to send to owner.</p>
                </div>
              )}
              {previewUrl ? (
                <div className="border rounded bg-gray-50">
                  <object data={previewUrl} type="application/pdf" className="w-full rounded" style={{ height: 500 }}>
                    <p className="text-sm text-muted-foreground p-4">Unable to preview PDF in browser.</p>
                  </object>
                </div>
              ) : (
                <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
              )}
            </>
          ) : null}
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          {step === 'configure' ? (
            <>
              <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
              <Button onClick={handlePreview} disabled={generating || loading} className="bg-primary hover:bg-primary/90 text-white" data-testid="preview-contract-btn">
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Eye className="w-4 h-4 mr-2" /> Preview Contract</>}
              </Button>
            </>
          ) : (
            <>
              {isLiveDraft || !previewData?.contract_status || previewData?.contract_status === 'draft' ? (
                <>
                  <Button variant="outline" onClick={() => { setStep('configure'); setExistingFileId(null); }}>
                    {existingFileId ? 'Edit & Regenerate' : 'Back to Edit'}
                  </Button>
                  <Button onClick={handleApprove} disabled={approving} className="bg-primary hover:bg-primary/90 text-white" data-testid="approve-contract-btn">
                    {approving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Approving...</> : 'Approve Contract'}
                  </Button>
                </>
              ) : previewData?.contract_internally_signed ? (
                <>
                  <Button variant="outline" onClick={() => onClose(false)}>Close</Button>
                  <Button onClick={() => { onGenerated?.(); onClose(true); }} className="bg-primary hover:bg-primary/90 text-white" data-testid="send-to-owner-btn">
                    Send to Owner
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => onClose(false)}>Close</Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsignmentContractDialog;
